import { can, daysUntil, rolesWith, shortDate, signerNames, type Assessment, type Capability, type MyWorkItem, type MyWorkKind, type Vendor } from '../../lib/api'
import { AWAITING_APPROVAL, FINISHED, LATE, TASK_NOUN, TASK_VERB, WAITING_FOR_YOU, WAITING_ON_US, WITH_VENDOR, chipWord, needsSomebody, statusWord, taskVerb, type StatusWord } from '../../lib/status'
import { dueDiligenceRefusal, isRequested } from '../../lib/requestRules'
import { rowLate, whenWords } from '../../views/MyWork'
import { signsOnApproval, type Place } from './sections'

// What to do next on one vendor, worked out once (release 3.2, design rule 1:
// one screen, one next step).
//
// # WHY ONE LIST
//
// The vendor page offered the same task up to four times at once: the header's
// button, the Next steps panel, the line in the Risk part and the "Not sent"
// panel, each with its own words and two of them with their own copy of the
// resend call. The header button and the Next steps panel are now one thing,
// the Next step bar, drawn from this list, and the first item is the primary
// action. Exits are not worked out here: a proposal or a sign-off is decided in
// the exit workspace, and reaches this bar from My work (withMyWork below).
//
// A step with nothing for us to do still says what happens next ("waiting on
// the vendor until 8 Oct"), so a page is never silent about whose turn it is.
//
// The list is drawn as one line above every section, a few words and a button
// for each step, and as a sentence for each under that line (NextStep.tsx).
//
// # THE BAR AGREES WITH MY WORK
//
// The steps worked out here cover the questionnaire and the approval. Anything
// else My work lists for this vendor (a document to review, a finding, a tier
// change, an accepted risk, an exit to decide or sign) is added to the bar from
// the vendor's own My work rows, so the page never says "Nothing is waiting"
// while My work lists a task on it (review of PR 24, M5). The approval has one
// door: the vendor's Profile, where Approve and Reject are, which is where the
// My work row opens too.
//
// # ONE NEXT STEP PER STAGE (RELEASE 3.3, C4 OF THE 12 OCT PLAN)
//
// A new vendor's bar said both "Nothing has been sent... the questionnaire is
// the first step of due diligence" and "Not yet approved. Submit it for
// approval once due diligence is done": two next steps for one vendor, and the
// second one a step nobody could take yet. Each stage now has one:
//
//   Requested                   the TPRM lead's decision ("Approve request",
//                               or "Waiting on TPRM lead" for anybody else)
//   Approved for due diligence  send the questionnaire
//   In due diligence            the questionnaire's own steps, and submitting
//   Awaiting approval           the approval
//
// and the send and reassess steps are offered only where the server's gate
// (lib/requestRules.ts, the mirror of domain.CheckDueDiligence) would take them.

export type StepAct =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'go'; label: string; place: Place }
  | { kind: 'chase'; assessmentId: string }
  // Opens the Approve request drawer on this vendor (RequestDrawer.tsx).
  | { kind: 'request'; label: string; vendorId: string }
  | { kind: 'none' }

export type Step = {
  key: string
  // What is waiting, in a few words: the state and never the verb, which is on
  // the button. The record's one line (NowLine.tsx) has room for this and not
  // for the sentence.
  what: string
  // Which one: the questionnaire's reference, or My work's own words for the
  // row. Two accepted risks to decide are told apart by this and nothing else,
  // so the line shows it and does not leave it to the sentence.
  detail?: string
  // The detail is the reason of a decision, and the line draws all of it. A
  // title can lose its tail; a reason cut short can say the opposite.
  whole?: boolean
  // How far past its date, where it is. The one line draws it with the clock a
  // late chip carries, because the sentence that said so is one press away. A
  // step of the vendor's own counts the days; one from My work says it as My
  // work does (lateBy). lateDays is the same lateness as a number, for the
  // order: the most overdue is read first.
  late?: string
  lateDays?: number
  // A state that needs somebody though nothing is late: blocked, refused,
  // expired. The chip My work draws the row with, where needsSomebody
  // (lib/status.ts) says a reader must not miss it. Treated as a late step
  // is: read first, said in the sentence, counted by the note.
  alarm?: StatusWord
  // One sentence: what is true and what this does.
  text: string
  act: StepAct
  // Whose turn it is: the reader's, the vendor's, a second person's, or a role
  // the reader does not hold (named in who).
  turn: 'us' | 'vendor' | 'approver' | 'role'
  who?: string
  // The action is on offer but is not the next thing to do: a reminder while
  // the vendor still has time. Drawn as an ordinary button, never the primary.
  quiet?: boolean
}

function days(n: number): string {
  return Math.abs(n) + (Math.abs(n) === 1 ? ' day' : ' days')
}

// A questionnaire's state in the vocabulary's own word: "Questionnaire submitted".
function questionnaireIs(status: string): string {
  return 'Questionnaire ' + statusWord('questionnaire', status).word.toLowerCase()
}

// Whose turn a step of ours is, and its button, from one answer: whether the
// reader holds what the step needs. An analyst on a draft vendor was told
// "Your turn 2" and given one button, because the turn was decided in one place
// and the button in another (review of 11 Oct 2026). A step the reader cannot
// take is the turn of the first role in the table that can (the table lists the
// roles a tenant is given first), and keeps only a button that opens something
// to read.
function taken(need: Capability, act: StepAct, otherwise: StepAct = { kind: 'none' }): Pick<Step, 'turn' | 'who' | 'act'> {
  if (can(need)) return { turn: 'us', act }
  const first = rolesWith(need)[0]
  return { turn: 'role', who: first ? signerNames([first])[0] : undefined, act: otherwise }
}

export function nextSteps(vendor: Vendor, assessments: Assessment[]): Step[] {
  const out: Step[] = []
  const issue = can('questionnaire.issue')

  // Requested: the one step is the TPRM lead's decision. Nothing else on the
  // vendor can move until it is made, so nothing else is offered.
  if (isRequested(vendor)) {
    const lead = can('vendor.request_approve')
    out.push({
      key: 'request',
      what: 'Request waiting',
      text: lead
        ? 'Nobody has approved this request yet. Approve it so due diligence can begin, or reject it with a reason.'
        : 'Nobody has approved this request yet, so no questionnaire can be sent.',
      ...(lead
        ? { turn: 'us' as const, act: { kind: 'request' as const, label: 'Approve request', vendorId: vendor.id } }
        : { turn: 'role' as const, who: 'TPRM lead', act: { kind: 'none' as const } }),
    })
    return out
  }
  // The gate the server asks before any questionnaire goes, with a reason
  // standing in for the one a suspended vendor is asked for on the send
  // screen: a step that sends is offered only where the send would be taken.
  const mayBeSent = dueDiligenceRefusal(vendor, 'given on the send screen') == null

  const review = assessments.find((a) => WAITING_ON_US.has(a.status))
  if (review) {
    out.push({
      key: 'review',
      what: questionnaireIs(review.status),
      detail: review.reference,
      text: 'Answers to questionnaire ' + review.reference + ' came back' + (review.submittedAt ? ' on ' + shortDate(review.submittedAt) : '') + '. Review and score them.',
      ...taken(
        'assessment.score',
        { kind: 'link', label: TASK_VERB.review, href: '#/assessment/' + review.id },
        { kind: 'link', label: 'Open questionnaire', href: '#/assessment/' + review.id },
      ),
    })
  }

  const out1 = assessments.find((a) => WITH_VENDOR.has(a.status))
  if (out1) {
    const d = daysUntil(out1.dueOn)
    const late = d != null && d < 0
    if (late && out1.status !== 'RETURNED') {
      out.push({
        key: 'chase',
        what: 'Questionnaire',
        detail: out1.reference,
        late: days(d) + ' overdue',
        lateDays: Math.abs(d),
        text: 'Questionnaire ' + out1.reference + ' is ' + days(d) + ' overdue. A reminder sends the vendor the invitation again.',
        ...taken('questionnaire.issue', { kind: 'chase', assessmentId: out1.id }, { kind: 'link', label: 'Open questionnaire', href: '#/assessment/' + out1.id }),
      })
    } else if (out1.status === 'RETURNED') {
      out.push({
        key: 'clarify',
        turn: 'vendor',
        what: questionnaireIs(out1.status),
        detail: out1.reference,
        late: late ? days(d) + ' overdue' : undefined,
        lateDays: late ? Math.abs(d) : undefined,
        text:
          'Questionnaire ' + out1.reference + ' needs clarification from the vendor' + (late ? ', ' + days(d) + ' overdue.' : out1.dueOn ? ', due ' + shortDate(out1.dueOn) + '.' : '.'),
        act: { kind: 'link', label: 'Open questionnaire', href: '#/assessment/' + out1.id },
      })
    } else {
      // Sent or in progress and not yet due. The reminder is on offer from the
      // day it is sent (an invitation that went astray, or mail that was off
      // when it was sent), quietly: the vendor still has time.
      out.push({
        key: 'waiting',
        turn: 'vendor',
        what: questionnaireIs(out1.status) + (out1.dueOn ? ', due ' + shortDate(out1.dueOn) : ''),
        detail: out1.reference,
        text:
          // The turn label already says it waits on the vendor.
          'Questionnaire ' +
          out1.reference +
          (out1.dueOn ? ' is due ' + shortDate(out1.dueOn) : ' is with the vendor') +
          '. Their answers will show here and in My work.' +
          (issue ? ' If the invitation did not reach them, a reminder sends it again.' : ''),
        act: issue ? { kind: 'chase', assessmentId: out1.id } : { kind: 'none' },
        quiet: true,
      })
    }
  }

  if (assessments.length === 0 && mayBeSent) {
    // Said only where the vendor says its request is approved: an API older
    // than the field says nothing, and the stage word then says "Requested".
    const approvedRequest = vendor.status === 'DRAFT' && vendor.requestApproved === true
    out.push({
      key: 'send',
      what: approvedRequest ? 'Approved for due diligence' : questionnaireIs('NOT_SENT'),
      text: approvedRequest
        ? 'The request is approved. Send the questionnaire to begin due diligence.'
        : 'Nothing has been sent to this vendor yet. The questionnaire is the first step of due diligence.',
      ...taken('questionnaire.issue', { kind: 'link', label: TASK_VERB.send, href: '#/vendor/' + vendor.id + '/invite' }),
    })
  }

  const due = daysUntil(vendor.nextDueOn)
  if (mayBeSent && due != null && due < 0 && !out1 && !review && assessments.some((a) => FINISHED.has(a.status))) {
    out.push({
      key: 'reassess',
      what: 'Reassessment',
      late: days(due) + ' overdue',
      lateDays: Math.abs(due),
      text: 'Reassessment is ' + days(due) + ' overdue.',
      ...taken('questionnaire.issue', { kind: 'link', label: TASK_VERB.send, href: '#/vendor/' + vendor.id + '/invite' }),
    })
  }

  // Submitting is a step once due diligence has begun: before a questionnaire
  // exists it is a step nobody can take yet, and saying it beside "send the
  // questionnaire" was the two next steps for one vendor C4 names.
  if ((vendor.status === 'DRAFT' && assessments.length > 0) || vendor.status === 'PENDING_APPROVAL') {
    const accept = vendor.currentBand === 'HIGH' || vendor.currentBand === 'CRITICAL'
    // A reader who may raise the written risk acceptance is taken to the
    // approval panel, where it is raised beside the sign-offs it unlocks
    // (release 3.2); everybody else to the Profile as before.
    const raise = accept && can('risk.accept')
    const open: StepAct = { kind: 'go', label: 'Open approval', place: raise ? SIGNING_PLACE : APPROVAL_PLACE }
    out.push({
      key: 'approve',
      what: vendor.status === 'DRAFT' ? 'Not yet approved' : AWAITING_APPROVAL,
      text: accept
        ? 'Rated ' +
          (vendor.currentBand === 'CRITICAL' ? 'critical' : 'high') +
          '. Open the approval for the sign-offs it needs and any written risk acceptance' +
          (raise ? ', which is raised there.' : '.')
        : vendor.status === 'DRAFT'
          ? 'Not yet approved. Submit it for approval once due diligence is done.'
          : 'Awaiting approval. The approver sees it in My work.',
      // The Profile holds Approve and Reject and links to the activation
      // checklist when that holds the approval: the one door, the one My work
      // opens too.
      // "Approve" (My work's verb) only once My work says the decision is the
      // reader's (withMyWork): holding vendor.approve is not enough when the
      // reader made the move being decided.
      //
      // Not yet submitted, it is the turn of whoever may submit it, which is
      // an edit of the vendor. Anybody may open the approval to read it.
      ...(vendor.status === 'PENDING_APPROVAL' ? { turn: 'approver' as const, act: open } : taken('vendor.edit', open, open)),
    })
  }

  return out
}

// Where a vendor's approval is decided: the Profile, as My work's row opens it
// (#/vendor/{id}/full/profile).
export const APPROVAL_PLACE: Place = { section: 'profile', part: 'profile' }

// Where the approval chain is signed and a written risk acceptance or a
// contract waiver is raised: the Approval part of the Contracts tab.
export const SIGNING_PLACE: Place = { section: 'contracts', part: 'approval' }

// Whether a vendor-approval row is a stage's sign-off: sections.ts says, by the
// address, for this page and for My work's verb alike.
export { signsOnApproval }

// The step keys that already say each kind of My work row, so the row is not
// said twice. A row of a kind not here, or whose step the vendor's state did not
// produce, is added to the bar.
const SAID_BY: Partial<Record<MyWorkKind, string[]>> = {
  review: ['review'],
  chase: ['chase', 'clarify', 'waiting'],
  send: ['send', 'reassess'],
  'vendor-approval': ['approve'],
  'vendor-request': ['request'],
}

// Whether a My work row is late and in what words, as My work draws the row:
// "9 days late" from its date (whenWords), or the chip's own Overdue where the
// row has no date and its chip still says so. A second rule here would let the
// vendor's line and My work disagree about the same row; the line had none at
// all, and a finding nine days overdue read "Finding, Open finding" (review of
// 11 Oct 2026).
export function lateWords(it: MyWorkItem): string | undefined {
  return lateBy(it)?.late
}

// The same answer with the days as a number, read out of My work's own words
// ("9 days late") so there is no second sum to disagree with them. A row late
// by its chip alone has no number: it is late all the same, and is read after
// the ones whose days are known.
function lateBy(it: MyWorkItem): Pick<Step, 'late' | 'lateDays'> | undefined {
  const when = whenWords(it)
  if (when.late) return { late: when.text, lateDays: Number(/^\d+/.exec(when.text)?.[0] ?? 0) }
  const chip = chipWord(it.status.domain, it.status.code, rowLate(it))
  return chip.word === LATE ? { late: chip.word, lateDays: 0 } : undefined
}

// What a My work row carries that a reader must not miss: its lateness, and a
// chip that needs somebody for another reason. needsSomebody is the rule Home's
// tabs mark by, so the day a row arrives blocked or expired the line says so
// without having been taught the word.
function marksOf(it: MyWorkItem): Pick<Step, 'late' | 'lateDays' | 'alarm'> {
  const chip = chipWord(it.status.domain, it.status.code, rowLate(it))
  return { ...lateBy(it), alarm: needsSomebody(chip) && chip.word !== LATE ? chip : undefined }
}

// Whether a step is one of those: late, or in a state that needs somebody.
export const urgent = (s: Pick<Step, 'late' | 'alarm'>) => Boolean(s.late || s.alarm)

// A sentence about something late or blocked says so. The line marks such a
// step with a chip, and a step the line has no room for gets no chip: opened
// in Details, a finding nine days overdue read as any other (review of
// 11 Oct 2026).
function andMarks(text: string, s: Pick<Step, 'late' | 'alarm'>): string {
  const said = [s.late, s.alarm?.word].filter((w): w is string => Boolean(w)).map((w) => w.charAt(0).toUpperCase() + w.slice(1) + '.')
  return [text, ...said].join(' ')
}

// The bar's steps with the vendor's own My work rows that no step says yet.
// A vendor approval My work gives the reader makes the approval step theirs.
export function withMyWork(steps: Step[], items: MyWorkItem[] | null | undefined): Step[] {
  if (!items || items.length === 0) return steps
  const have = new Set(steps.map((s) => s.key))
  const seen = new Set<string>()
  let out = [...steps]
  for (const it of items) {
    if (it.group === 'notice' || seen.has(it.key)) continue
    seen.add(it.key)
    if ((SAID_BY[it.kind] ?? []).some((k) => have.has(k))) {
      if (it.kind === 'vendor-approval') {
        // A signature the reader gives (a stage of the approval chain: TPRM,
        // Security and risk, the business owner) is given on the approval
        // panel, and My work's row says which one. Admitting the vendor once
        // every stage is signed is the Profile's Approve. Saying "approve or
        // reject it on the Profile" to a business owner whose turn is the
        // business sign-off sent them to a button they do not have (found
        // walking the CPO's flows, release 3.2).
        const signing = signsOnApproval(it.action.href)
        out = out.map((s) =>
          s.key === 'approve' && s.act.kind === 'go'
            ? {
                ...s,
                turn: 'us',
                who: undefined,
                what: TASK_NOUN['vendor-approval'],
                detail: signing ? it.what || undefined : undefined,
                whole: true,
                ...marksOf(it),
                text: andMarks(
                  signing
                    ? (it.what || 'Your sign-off is waiting') + '. Give it on the Contracts tab, under Approval.'
                    : 'Awaiting approval, and yours to decide. Approve or reject it on the Profile.',
                  marksOf(it),
                ),
                act: {
                  ...s.act,
                  // My work's verb for this very row: Sign for a stage's
                  // sign-off, Approve for the vendor's admission.
                  label: taskVerb(it),
                  // Approve and Reject are on the Profile, whatever the step
                  // opened before My work said the decision is the reader's.
                  place: signing ? SIGNING_PLACE : APPROVAL_PLACE,
                },
              }
            : s,
        )
      }
      continue
    }
    out.push({
      key: 'my-work:' + it.key,
      turn: 'us',
      what: TASK_NOUN[it.kind] ?? 'Task',
      detail: it.what || undefined,
      // What My work files under Decide is a decision, and its words are the
      // reason for it. A risk acceptance's are too, where it is filed to be
      // reviewed or closed.
      whole: it.group === 'decide' || it.kind === 'exception-review' || it.kind === 'exception-closure',
      ...marksOf(it),
      text: andMarks((TASK_NOUN[it.kind] ?? 'Task') + (it.what ? ': ' + it.what : '') + '.', marksOf(it)),
      act:
        it.action.kind === 'chase' && it.assessmentId
          ? { kind: 'chase', assessmentId: it.assessmentId }
          : { kind: 'link', label: taskVerb(it), href: it.action.href },
    })
  }
  return out
}

// ------------------------------------------------------- order, and the fold
//
// The line has room for some of the steps and says the rest are in Details.
// What it leaves off was whatever came last, and the order had no idea of
// urgency: the reader's own finding, nine days overdue and fifth of six, was
// behind "and 2 more in Details" with nothing to say it was late (review of
// 11 Oct 2026). Three rules stop that, each a function of the steps alone so
// the line and the list under Details cannot differ.

const mine = (s: Step) => s.turn === 'us'

// The order the steps are read in, on the line and under Details alike: the
// late and the blocked first, the most overdue of those first and one with no
// count of days after those that have one, then the reader's own, then the
// rest. Steps that tie keep the order they came in, so nothing moves between
// two draws of the same steps.
export function inBarOrder(steps: Step[]): Step[] {
  const tier = (s: Step) => (urgent(s) ? 0 : mine(s) ? 1 : 2)
  return steps
    .map((s, at) => ({ s, at }))
    .sort((a, b) => tier(a.s) - tier(b.s) || (urgent(a.s) && urgent(b.s) ? (b.s.lateDays ?? 0) - (a.s.lateDays ?? 0) : 0) || a.at - b.at)
    .map((x) => x.s)
}

// Which steps the line keeps when it has room for fewer than all: the
// reader's own before anybody else's, and the late and blocked before the
// rest, so a step of the reader's is never left off while another role's is
// drawn.
function keepOrder(steps: Step[]): Step[] {
  const rank = (s: Step) => (mine(s) ? 0 : 2) + (urgent(s) ? 0 : 1)
  return inBarOrder(steps)
    .map((s, at) => ({ s, at }))
    .sort((a, b) => rank(a.s) - rank(b.s) || a.at - b.at)
    .map((x) => x.s)
}

// The step whose button is the primary one: the first the line would keep
// that has something to do now, else the first with any action, else the
// first. So the primary button is never on a step the line has left off.
export function leadStep(steps: Step[]): Step | undefined {
  const kept = keepOrder(steps)
  return kept.find((s) => s.act.kind !== 'none' && !s.quiet) ?? kept.find((s) => s.act.kind !== 'none') ?? kept[0]
}

// What the line draws when it has room for `limit` steps, and what it leaves
// for Details, each in the order they are read in. With no limit, all of them.
export function onLine(steps: Step[], limit?: number | null): { shown: Step[]; hidden: Step[] } {
  const ordered = inBarOrder(steps)
  if (limit == null || limit >= ordered.length) return { shown: ordered, hidden: [] }
  const kept = new Set(keepOrder(steps).slice(0, Math.max(1, limit)))
  return { shown: ordered.filter((s) => kept.has(s)), hidden: ordered.filter((s) => !kept.has(s)) }
}

// The words for what the line left off. Never a bare count when it matters
// what they are: how many are overdue, or in a state that needs somebody, by
// that state's own word; and failing those, how many are the reader's own.
// A step that is late counts as overdue whether or not its days are known.
export function restNote(hidden: Step[]): string {
  const marks = new Map<string, number>()
  for (const s of hidden) {
    const word = s.late ? 'overdue' : s.alarm?.word.toLowerCase()
    if (word) marks.set(word, (marks.get(word) ?? 0) + 1)
  }
  const yours = hidden.filter(mine).length
  const said = marks.size > 0 ? [...marks].map(([word, n]) => n + ' ' + word) : yours === 0 ? [] : [hidden.length === 1 ? 'yours' : yours + ' of them yours']
  return ['and ' + hidden.length + ' more in Details', ...said].join(', ')
}

// ------------------------------------------------------- how much of a detail
//
// The line was cutting a step's detail at a width, with an ellipsis. "Risk
// accepted in writing is not enough." drew as "Risk accepted in...", which
// says the opposite, and the whole of it was a hover away, which a keyboard
// and a phone do not have (second review of 11 Oct 2026). The rules now:
//
//  - The reason of a decision is never cut. It wraps.
//  - Nor is anything with a word in it that turns the meaning round or says
//    something is wrong: "Backups are taken but not tested at the second site"
//    cut to "Backups are taken..." is good news made of bad. Keeping the word
//    and cutting after it was tried and is not enough: "No data processing..."
//    for "No data processing agreement on file".
//  - What is left (a document's name, a title with no such word) may lose its
//    tail, at a word and never before its third.
//  - What is cut is a button that opens Details at that step, so the rest is a
//    press away for a keyboard and a finger too.
//
// The list of words is a floor, not a grammar: it errs towards drawing the
// whole. It is why a reason is whole however it is worded.

const TURNS =
  /^(?:not|no|never|none|nor|neither|nothing|nobody|without|cannot|unless|except|but|only|missing|lacks?|lacking|absent|fail(?:s|ed|ing|ure)?|expired|refused|rejected|denied|declined|unable|insufficient|inadequate|incomplete|invalid|outdated|overdue|disabled|non-?\p{L}+|un(?:encrypted|signed|patched|supported|tested|protected|verified|restricted|monitored))$|n't$/iu

function turns(word: string): boolean {
  return TURNS.test(word.replace(/\u2019/g, "'").replace(/[^\p{L}\p{N}'-]/gu, ''))
}

// The detail's words the line may keep to: six, or three once the steps did
// not fit and are being drawn tighter (FittedSteps).
const WORDS = { loose: 6, tight: 3 }

// The detail as the line draws it, and whether that is less than all of it.
export function detailOnLine(s: Pick<Step, 'detail' | 'whole'>, tight = false): { text: string; cut: boolean } | null {
  const all = (s.detail ?? '').trim()
  if (all === '') return null
  const words = all.split(/\s+/)
  const keep = tight ? WORDS.tight : WORDS.loose
  // Nothing is saved by cutting off a single word.
  if (s.whole || words.some(turns) || words.length - keep <= 1) return { text: all, cut: false }
  return { text: words.slice(0, keep).join(' ').replace(/[,;:.]+$/, '') + '...', cut: true }
}

// How many steps wait on the reader: the ones the list labels "Waiting for you", by
// the same field, so the count on the one line cannot disagree with the list
// behind it. A step is the reader's only where they can take it (taken, above,
// and My work's own rows), so the count is never of a step with no button.
export function yourTurnCount(steps: Step[]): number {
  return steps.filter((s) => s.turn === 'us').length
}

// Whose turn a step is, in words: who it waits on (glossary, 7 Oct plan 2.6;
// it said "Your turn", "Vendor's turn", "Approver's turn"). The reader's is
// the console's one phrase for it, WAITING_FOR_YOU, as My work and the risk
// acceptances page say it; the others wait on somebody, as the chip "Waiting
// on vendor" does.
export function turnWords(s: Pick<Step, 'turn' | 'who'>): string {
  switch (s.turn) {
    case 'us':
      return WAITING_FOR_YOU
    case 'vendor':
      return 'Waiting on vendor'
    case 'approver':
      return 'Waiting on approver'
    default:
      return 'Waiting on ' + (s.who || 'another role')
  }
}
